// ─────────────────────────────────────────────────────────────
// useVehicles — single source of truth for brand/model dropdowns.
// Fetches GET /api/parts/vehicles/ ONCE (module-level cache, shared
// across every page) and falls back to the bundled static catalog
// if the API is unreachable. Returns { brands: [...], models: {...} }.
// ─────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { getVehicles } from '../services/api';
import { VEHICLE_BRANDS, VEHICLE_MODELS } from '../models/vehicleModel';

const FALLBACK = { brands: VEHICLE_BRANDS, models: VEHICLE_MODELS, engines: {} };

let _cache = null;     // resolved { brands, models }
let _promise = null;   // in-flight fetch shared by all callers

function load() {
  if (_cache) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = getVehicles()
      .then((res) => {
        const d = res.data || {};
        // If the DB hasn't been seeded yet, keep the static fallback.
        _cache = (d.brands && d.brands.length) ? { engines: {}, ...d } : FALLBACK;
        return _cache;
      })
      .catch(() => {
        _cache = FALLBACK;     // offline / API error → bundled catalog
        return _cache;
      });
  }
  return _promise;
}

export function useVehicles() {
  const [data, setData] = useState(_cache || FALLBACK);

  useEffect(() => {
    let active = true;
    load().then((d) => { if (active) setData(d); });
    return () => { active = false; };
  }, []);

  return data;   // { brands, models }
}
